import {
  collection,
  doc,
  getDocs,
  addDoc,
  updateDoc,
  query,
  where,
  orderBy,
  Timestamp,
} from 'firebase/firestore';
import { db } from '../config/firebase';
import { Invitation, InvitationType, Role } from '@easydone/shared';
import { nanoid } from 'nanoid';
import { timestampToMillis } from '../utils/timestamp';

export interface CreateInvitationData {
  type: InvitationType;
  role: Role;
  expiresAt: Date;
  maxUses?: number;
  notes?: string;
  customMessage?: string;
}

/**
 * Generate a unique invitation token
 */
function generateToken(): string {
  return nanoid(12); // 12 character random string
}

/**
 * Create a new invitation
 */
export async function createInvitation(
  orgId: string,
  userId: string,
  data: CreateInvitationData
): Promise<Invitation> {
  // Store in top-level collection for easy token lookup
  const invitationsRef = collection(db, 'invitations');

  const token = generateToken();

  const invitationData: any = {
    orgId,
    token,
    type: data.type,
    role: data.role,
    expiresAt: Timestamp.fromDate(data.expiresAt),
    usedCount: 0,
    isActive: true,
    createdBy: userId,
    createdAt: Timestamp.now(),
  };

  // Only add optional fields if they have values (Firestore doesn't accept undefined)
  if (data.maxUses !== undefined) {
    invitationData.maxUses = data.maxUses;
  }
  if (data.notes) {
    invitationData.notes = data.notes;
  }
  if (data.customMessage) {
    invitationData.customMessage = data.customMessage;
  }

  const docRef = await addDoc(invitationsRef, invitationData);

  return {
    id: docRef.id,
    ...invitationData,
  } as Invitation;
}

/**
 * Get all invitations for an organization
 */
export async function getInvitations(orgId: string): Promise<Invitation[]> {
  const invitationsRef = collection(db, 'invitations');
  const q = query(
    invitationsRef,
    where('orgId', '==', orgId),
    orderBy('createdAt', 'desc')
  );

  const snapshot = await getDocs(q);
  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  } as Invitation));
}

/**
 * Get invitation by token
 */
export async function getInvitationByToken(token: string): Promise<Invitation | null> {
  // We need to search across all organizations
  // This is a limitation - we'll need to know the orgId or use a different approach
  // For now, let's create a top-level invitations collection indexed by token
  const invitationsRef = collection(db, 'invitations');
  const q = query(invitationsRef, where('token', '==', token));

  const snapshot = await getDocs(q);
  if (snapshot.empty) return null;

  const doc = snapshot.docs[0];
  return {
    id: doc.id,
    ...doc.data(),
  } as Invitation;
}

/**
 * Validate and use an invitation
 */
export async function useInvitation(token: string, userId: string): Promise<{
  valid: boolean;
  invitation?: Invitation;
  error?: string;
}> {
  const invitation = await getInvitationByToken(token);

  if (!invitation) {
    return { valid: false, error: 'Invitation not found' };
  }

  if (!invitation.isActive) {
    return { valid: false, error: 'Invitation has been revoked' };
  }

  const now = Date.now();
  const expiresAt = timestampToMillis(invitation.expiresAt);

  if (expiresAt < now) {
    return { valid: false, error: 'Invitation has expired' };
  }

  if (invitation.maxUses && invitation.usedCount >= invitation.maxUses) {
    return { valid: false, error: 'Invitation has reached maximum uses' };
  }

  // Update usage count
  const invitationRef = doc(db, 'invitations', invitation.id);
  await updateDoc(invitationRef, {
    usedCount: invitation.usedCount + 1,
    updatedAt: Timestamp.now(),
  });

  // Record usage in sub-collection
  const usageRef = collection(db, 'invitations', invitation.id, 'usage');
  await addDoc(usageRef, {
    invitationId: invitation.id,
    userId,
    usedAt: Timestamp.now(),
  });

  return {
    valid: true,
    invitation: {
      ...invitation,
      usedCount: invitation.usedCount + 1,
    },
  };
}

/**
 * Revoke an invitation
 */
export async function revokeInvitation(invitationId: string): Promise<void> {
  const invitationRef = doc(db, 'invitations', invitationId);
  await updateDoc(invitationRef, {
    isActive: false,
    updatedAt: Timestamp.now(),
  });
}

/**
 * Delete an invitation
 */
export async function deleteInvitation(invitationId: string): Promise<void> {
  const invitationRef = doc(db, 'invitations', invitationId);
  await updateDoc(invitationRef, {
    isActive: false,
    updatedAt: Timestamp.now(),
  });
}

/**
 * Get invitation usage stats
 */
export async function getInvitationUsage(invitationId: string) {
  const usageRef = collection(db, 'invitations', invitationId, 'usage');
  const snapshot = await getDocs(usageRef);

  return snapshot.docs.map(doc => ({
    id: doc.id,
    ...doc.data(),
  }));
}
